import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import { api, authorizedFetch } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import { PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from "lucide-react";
import { RestoreIcon } from "@/components/icons/RestoreIcon";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";
const PAGE_SIZE = 25;

interface CargoTypeRow {
  id: string;
  name: string;
  code: string;
  cargoDesc: "PE" | "NP" | null | undefined;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = "active" | "deleted" | "all";

type FormState = {
  name: string;
  code: string;
  cargoDesc: string | null | undefined;
};

const EMPTY_FORM: FormState = {
  name: "",
  code: "",
  cargoDesc: "",
};

export default function CargoTypesPage() {
  const qc = useQueryClient();
  const { can } = usePermissions();

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active");
  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<CargoTypeRow | null>(null);
  const [deleting, setDeleting] = useState<CargoTypeRow | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const { data: cargoTypes = [], isLoading } = useQuery({
    queryKey: ["cargos", "types", filterStatus],
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (filterStatus === "active") query.deleted = "false";
      else if (filterStatus === "deleted") query.deleted = "true";

      const res = await api.cargos.types.$get({ query });
      if (!res.ok) throw new Error("Error loading cargo types");
      return res.json() as Promise<CargoTypeRow[]>;
    },
  });

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return cargoTypes.filter((item) => {
      if (!term) return true;
      return (
        item.name.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term)
      );
    });
  }, [cargoTypes, search]);

  useEffect(() => { setPage(1); }, [search, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const createMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await api.cargos.types.$post({
        json: { name: data.name, code: data.code, cargoDesc: (data.cargoDesc || null) as "PE" | "NP" | null },
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargos", "types"] });
      closeSheet();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormState }) => {
      const res = await api.cargos.types[":id"].$patch({
        param: { id },
        json: { name: data.name, code: data.code, cargoDesc: (data.cargoDesc || null) as "PE" | "NP" | null },
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargos", "types"] });
      closeSheet();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await api.cargos.types[":id"].$delete({ param: { id } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargos", "types"] });
      setDeleteOpen(false);
      setDeleting(null);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id);
      await authorizedFetch(`${API_URL}/api/cargos/types/${id}/restore`, {
        method: "PATCH",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargos", "types"] });
      setRestoringId(null);
    },
    onError: (err: Error) => {
      setFormError(err.message);
      setRestoringId(null);
    },
  });

  function closeSheet() {
    setSheetOpen(false);
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(item: CargoTypeRow) {
    setEditing(item);
    setForm({ name: item.name, code: item.code, cargoDesc: item.cargoDesc ?? "" });
    setFormError(null);
    setSheetOpen(true);
  }

  function openDelete(item: CargoTypeRow) {
    setDeleting(item);
    setDeleteOpen(true);
  }

  function sanitizeCode(next: string) {
    return next.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 10);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const payload = {
      name:      form.name.trim(),
      code:      sanitizeCode(form.code.trim()),
      cargoDesc: (form.cargoDesc || null) as "PE" | "NP" | null,
    };

    if (!payload.name || !payload.code) {
      setFormError("Completá nombre y código");
      return;
    }

    if (!/^[A-Z]+$/.test(payload.code)) {
      setFormError("El código solo puede contener letras");
      return;
    }

    if (payload.code.length > 10) {
      setFormError("El código debe tener hasta 10 letras");
      return;
    }

    if (editing) updateMut.mutate({ id: editing.id, data: payload as FormState });
    else createMut.mutate(payload as FormState);
  }

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <>
      <SiteHeader title="Tipos de carga" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
            <SelectTrigger className="w-40" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Eliminados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>

          {can("write", "cargo") && (
            <Button onClick={openCreate} className="gap-2">
              <PlusIcon className="size-4" /> Nuevo tipo
            </Button>
          )}
        </div>

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-24 text-center">Desc. carga</TableHead>
                {can("write", "cargo") && <TableHead className="w-32 text-right pr-4">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={can("write", "cargo") ? 4 : 3} className="text-center py-16 text-muted-foreground">
                    Cargando tipos de carga...
                  </TableCell>
                </TableRow>
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={can("write", "cargo") ? 4 : 3} className="text-center py-16 text-muted-foreground">
                    {search ? "No results for the search" : "No cargo types found"}
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((item) => (
                  <TableRow key={item.id} className={item.deletedAt ? "opacity-70" : undefined}>
                    <TableCell>
                      <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded uppercase tracking-wide">
                        {item.code}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-sm">{item.name}</TableCell>
                    <TableCell className="text-center">
                      {item.cargoDesc
                        ? <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{item.cargoDesc}</span>
                        : <span className="text-muted-foreground/40 text-xs">—</span>}
                    </TableCell>
                    {can("write", "cargo") && (
                      <TableCell className="text-right pr-4">
                        <div className="flex justify-end gap-1">
                          {item.deletedAt ? (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => restoreMut.mutate(item.id)}
                              disabled={restoringId === item.id}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Restaurar"
                            >
                              <RestoreIcon className="size-3.5" />
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} title="Editar">
                                <PencilIcon className="size-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => openDelete(item)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Eliminar"
                              >
                                <Trash2Icon className="size-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); else setSheetOpen(true); }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar tipo de carga" : "Nuevo tipo de carga"}</SheetTitle>
            <SheetDescription>Usá códigos cortos, solo letras y hasta 10 caracteres.</SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
            <div className="grid gap-1.5">
              <Label htmlFor="cargo-type-code">Código</Label>
              <Input
                id="cargo-type-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: sanitizeCode(e.target.value) }))}
                maxLength={10}
                className="uppercase font-mono"
                placeholder="PERECEDERO"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="cargo-type-name">Nombre</Label>
              <Input
                id="cargo-type-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={200}
                placeholder="Perecedero"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Descripción de la carga</Label>
              <Select
                value={form.cargoDesc || "__none__"}
                onValueChange={(v) => setForm((f) => ({ ...f, cargoDesc: v === "__none__" ? "" : (v ?? "") }))}
              >
                <SelectTrigger>
                  <SelectValue>
                    {form.cargoDesc === "PE" ? "PE"
                      : form.cargoDesc === "NP" ? "NP"
                      : <span className="text-muted-foreground">Sin descripción</span>}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin descripción</SelectItem>
                  <SelectItem value="PE">PE</SelectItem>
                  <SelectItem value="NP">NP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear tipo"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar tipo de carga</DialogTitle>
            <DialogDescription>
              ¿Eliminás el tipo <strong>{deleting?.code}</strong> — {deleting?.name}? Esta acción es reversible desde la opción de restaurar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
